import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CompanyProps } from "../types/company/company";
import { api } from "../services/api.service";

interface FormData {
  name?: string;
  industryType?: string;
  organizationtype?: string;
  sort?: string;
  emailVerification?: string;
}

interface companyState {
  companies: CompanyProps[];
  company: CompanyProps | null;
  noOfCompanies: number;
  noOfGeneralCompanies: number;
  noOfServiceProviders: number;
  loading: boolean;
  error: string | null;
}

const initialState: companyState = {
  companies: [],
  noOfCompanies: 0,
  noOfGeneralCompanies: 0,
  noOfServiceProviders: 0,
  company: null,
  loading: false,
  error: null,
};

export const fetchCompanies = createAsyncThunk<CompanyProps[], FormData>(
  "company/fetchcompanies",
  async ({ name, organizationtype, industryType, emailVerification, sort }) => {
    const params: any = {};
    if (name) params.name = name;
    if (organizationtype) params.organizationtype = organizationtype;
    if (industryType) params.industryType = industryType;
    if (emailVerification) params.emailVerification = emailVerification;
    if (sort) params.sort = sort;

    const data = await api.company.getAll(params);
    return data as CompanyProps[];
  }
);
export const fetchNoOfCompanies = createAsyncThunk<number>(
  "company/fetchNoOfCompanies",
  async () => {
    const data = await api.company.count();
    return data as number;
  }
);
export const fetchNoOfGeneralCompanies = createAsyncThunk<number>(
  "company/fetchNoOfGeneralCompanies",
  async () => {
    const data = await api.company.countGeneral();
    return data as number;
  }
);
export const fetchNoOfServiceproviderCompanies = createAsyncThunk<number>(
  "company/fetchNoOfServiceproviderCompanies",
  async () => {
    const data = await api.company.countServiceProviders();
    return data as number;
  }
);

export const fetchCompany = createAsyncThunk<CompanyProps, { id: number }>(
  "company/fetchcompany",
  async ({ id }) => {
    const data = await api.company.getById(id);
    return data as CompanyProps;
  }
);

export const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    addCompany: (state, action: PayloadAction<CompanyProps>) => {
      state.companies.push(action.payload);
    },
    removeCompany: (state, action: PayloadAction<CompanyProps>) => {
      const id = action?.payload?.id?.toString()
      state.companies = state.companies.filter(
        (company) => company.id !== id
      );
    },
    updateCompany: (state, action: PayloadAction<{ data: CompanyProps }>) => {
      state.companies = state.companies.map((company) => {
        if (company.id === action.payload.data.id) {
          return action.payload.data;
        }
        return company;
      });
      state.company = action.payload.data;
    },
  },

  extraReducers(builder) {
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        console.log("Fetched companies:", action.payload);

        // Map API response fields to expected format
        const mappedCompanies = action.payload.map((company: any) => ({
          ...company,
          isActive: company.status === 'active' ? 1 : 0,
          serviceProvider: company.service_provider === 1 || company.service_provider === true,
          // Keep both formats for compatibility
          status: company.status,
          service_provider: company.service_provider,
        }));

        state.companies = mappedCompanies;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        // Map API response fields to expected format
        const mappedCompany = {
          ...action.payload,
          isActive: (action.payload as any).status === 'active' ? 1 : 0,
          serviceProvider: (action.payload as any).service_provider === 1 || (action.payload as any).service_provider === true,
        };

        state.company = mappedCompany;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNoOfCompanies.rejected, (state, action) => {
        state.error = action.error.message!;
        state.loading = false;
      })
      .addCase(fetchNoOfCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNoOfCompanies.fulfilled, (state, action) => {
        state.noOfCompanies = action.payload;
        state.loading = false;
      })
      .addCase(fetchNoOfGeneralCompanies.rejected, (state, action) => {
        state.error = action.error.message!;
        state.loading = false;
      })
      .addCase(fetchNoOfGeneralCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNoOfGeneralCompanies.fulfilled, (state, action) => {
        state.noOfGeneralCompanies = action.payload;
        state.loading = false;
      })
      .addCase(fetchNoOfServiceproviderCompanies.rejected, (state, action) => {
        state.error = action.error.message!;
        state.loading = false;
      })
      .addCase(fetchNoOfServiceproviderCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNoOfServiceproviderCompanies.fulfilled, (state, action) => {
        state.noOfServiceProviders = action.payload;
        state.loading = false;
      });
  },
});

export const { addCompany, removeCompany, updateCompany } =
  companySlice.actions;

export default companySlice.reducer;
